/**
 * Media API - Tags
 *
 * Tag management endpoints: add, remove, bulk, suggestions.
 */

import { z } from 'zod'
import { TagTypeSchema } from './common'

// ============================================================================
// POST /v1/media/:id/tags (Add Tags)
// ============================================================================

export const TagInputSchema = z.object({
  tagType: TagTypeSchema,
  tagValue: z.string().min(1).max(255).trim()
})

export type TagInput = z.infer<typeof TagInputSchema>

export const AddTagsRequestSchema = z.object({
  id: z.string().min(1),
  tags: z.array(TagInputSchema).min(1).max(20)
})

export type AddTagsRequest = z.infer<typeof AddTagsRequestSchema>

export const TagResponseSchema = z.object({
  id: z.string(),
  tagType: TagTypeSchema,
  tagValue: z.string()
})

export type TagResponse = z.infer<typeof TagResponseSchema>

export const AddTagsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    mediaId: z.string(),
    tags: z.array(TagResponseSchema)
  })
})

export type AddTagsResponse = z.infer<typeof AddTagsResponseSchema>

// ============================================================================
// DELETE /v1/media/:id/tags/:tagId (Remove Tag)
// ============================================================================

export const RemoveTagRequestSchema = z.object({
  id: z.string().min(1),
  tagId: z.string().min(1)
})

export type RemoveTagRequest = z.infer<typeof RemoveTagRequestSchema>

export const RemoveTagResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    mediaId: z.string(),
    tagId: z.string()
  })
})

export type RemoveTagResponse = z.infer<typeof RemoveTagResponseSchema>

// ============================================================================
// POST /v1/media/bulk-tags (Bulk Tags)
// ============================================================================

export const BulkTagsRequestSchema = z.object({
  mediaIds: z.array(z.string().min(1)).min(1).max(50),
  tags: z.array(TagInputSchema).min(1).max(20)
})

export type BulkTagsRequest = z.infer<typeof BulkTagsRequestSchema>

export const BulkTagsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    successCount: z.number(),
    failedIds: z.array(z.string())
  })
})

export type BulkTagsResponse = z.infer<typeof BulkTagsResponseSchema>

// ============================================================================
// GET /v1/media/tag-suggestions
// ============================================================================

export const TagSuggestionsRequestSchema = z.object({
  query: z.string().min(1).max(100),
  tag_type: TagTypeSchema.optional(),
  limit: z.coerce.number().min(1).max(50).optional()
})

export type TagSuggestionsRequest = z.infer<typeof TagSuggestionsRequestSchema>

export const TagSuggestionSchema = z.object({
  tagValue: z.string(),
  tagType: TagTypeSchema,
  count: z.number()
})

export type TagSuggestion = z.infer<typeof TagSuggestionSchema>

export const TagSuggestionsResponseSchema = z.object({
  message: z.string(),
  data: z.array(TagSuggestionSchema)
})

export type TagSuggestionsResponse = z.infer<typeof TagSuggestionsResponseSchema>
